import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { FaSync, FaCopy } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import config from '../config';

const server_URL = config.SERVER_URL;

const AdminApi = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [toastState, setToastState] = useState({ open: false, message: '', severity: 'info' });

  const showToast = (message, severity = 'info') => {
    setToastState({ open: true, message, severity });
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(userData.role_id || null);
    
    // Only fetch API keys if user is role 1
    if (userData.role_id === 1) {
      fetchApiKeys();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchApiKeys = async () => {
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'list_all',
          user_role: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1' && data.data) {
        setApiKeys(data.data);
      } else {
        showToast(data.error || 'Failed to fetch API keys', 'error');
        setApiKeys([]);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
      showToast('Error fetching API keys', 'error');
      setApiKeys([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEnable = async (apiKeyId, currentEnabled) => {
    setTogglingId(apiKeyId);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'admin_toggle_enable',
          api_key_id: apiKeyId,
          user_id: userData.id,
          user_role: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast(data.message || 'API key status updated successfully', 'success');
        fetchApiKeys(); // Refresh the list
      } else {
        showToast(data.error || 'Failed to update API key status', 'error');
      }
    } catch (error) {
      console.error('Error toggling API key:', error);
      showToast('Error updating API key status', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRegenerate = async (apiKeyId, customerUsername) => {
    if (!window.confirm(`Are you sure you want to regenerate the API key for ${customerUsername}? The old key will no longer work.`)) {
      return;
    }

    setRegeneratingId(apiKeyId);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(server_URL + '/api/customer_api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'admin_regenerate',
          api_key_id: apiKeyId,
          user_id: userData.id,
          user_role: userData.role_id,
        }),
      });

      const data = await response.json();
      if (data.status === '1') {
        showToast(data.message || 'API key regenerated successfully', 'success');
        fetchApiKeys(); // Refresh the list
      } else {
        showToast(data.error || 'Failed to regenerate API key', 'error');
      }
    } catch (error) {
      console.error('Error regenerating API key:', error);
      showToast('Error regenerating API key', 'error');
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleCopyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    showToast('API key copied to clipboard', 'success');
  };

  // Only show API page if user role is 1 (admin)
  if (userRole !== 1) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert severity="error">
          <Typography variant="h6">Unauthorized Access</Typography>
          <Typography variant="body1">
            You do not have permission to access this page.
          </Typography>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Customer API Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage API keys for all customers.
          </Typography>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>API Key</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Created At</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography variant="h6" color="text.secondary">
                        No API keys found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  apiKeys.map((apiKey) => (
                    <TableRow key={apiKey.id} hover>
                      <TableCell>{apiKey.customer_username || 'N/A'}</TableCell>
                      <TableCell>{apiKey.customer_email || 'N/A'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {apiKey.api_key}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyApiKey(apiKey.api_key)}
                            title="Copy API Key"
                          >
                            <FaCopy size={14} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={apiKey.enabled === 1 ? 'Enabled' : 'Disabled'}
                          color={apiKey.enabled === 1 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {apiKey.created_at
                          ? new Date(apiKey.created_at).toLocaleString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            color={apiKey.enabled === 1 ? 'warning' : 'success'}
                            size="small"
                            onClick={() => handleToggleEnable(apiKey.id, apiKey.enabled)}
                            disabled={togglingId === apiKey.id}
                          >
                            {togglingId === apiKey.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              apiKey.enabled === 1 ? 'Disable' : 'Enable'
                            )}
                          </Button>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<FaSync />}
                            onClick={() => handleRegenerate(apiKey.id, apiKey.customer_username)}
                            disabled={regeneratingId === apiKey.id}
                          >
                            {regeneratingId === apiKey.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              'Regenerate'
                            )}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <Snackbar
        open={toastState.open}
        autoHideDuration={6000}
        onClose={() => setToastState({ ...toastState, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastState({ ...toastState, open: false })}
          severity={toastState.severity}
          sx={{ width: '100%' }}
        >
          {toastState.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AdminApi;

